import React, { useEffect, useState } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import Browse from "./routes/Browse";
import ListingDetail from "./routes/ListingDetail";
import CreateListing from "./routes/CreateListing";
import EditListing from "./routes/EditListing";
import MyListings from "./routes/MyListings";
import Favorites from "./routes/Favorites";
import Conversations from "./routes/Conversations";
import Chat from "./routes/Chat";
import Login from "./routes/Login";
import BottomNav from "./components/BottomNav";
import Header from "./components/Header";
import FloatingAIButton from "./components/FloatingAIButton";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { supabase } from "./lib/supabase";
import ProfilePage from "./routes/ProfilePage";
import PurchaseHistory from "./routes/PurchaseHistory";
import SalesHistory from "./routes/SalesHistory";

function AuthCallback() {
  const navigate = useNavigate();
  
  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Handle the auth callback
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Auth error:', error);
          navigate('/login');
          return;
        }
        
        if (data.session) {
          navigate('/browse');
        } else {
          navigate('/login');
        }
      } catch (err) {
        console.error('Callback error:', err);
        navigate('/login');
      }
    };
    
    handleAuthCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="text-center">
        <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <span className="text-white text-3xl">👶</span>
        </div>
        <p className="text-slate-600">Completing sign in...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white text-3xl">👶</span>
          </div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? children : null;
}

export default function App() {
  const location = useLocation();
  const isLogin = location.pathname === "/" || location.pathname === "/login";
  const isCallback = location.pathname === "/auth/callback";
  const isChat = location.pathname.startsWith("/messages/") && location.pathname !== "/messages";
  const [searchQuery, setSearchQuery] = useState("");
  const { user, loading, isAuthenticated } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Debug auth state
  useEffect(() => {
    console.log('🔐 Auth state changed:', { 
      user: user?.id || 'null', 
      loading, 
      isAuthenticated,
      pathname: location.pathname 
    });
  }, [user, loading, isAuthenticated, location.pathname]);
  
  // Debug unread count state changes
  useEffect(() => {
    console.log('🔄 Unread count state changed to:', unreadCount);
  }, [unreadCount]);
  
  // Function to refresh unread count (expose globally for other components)
  const refreshUnreadCount = async () => {
    console.log('🔄 refreshUnreadCount called, user state:', { 
      user: user?.id || 'null', 
      loading, 
      isAuthenticated 
    });
    
    // Always get fresh user from supabase to avoid state issues
    const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !currentUser) {
      console.log('🔄 No user found, setting unread count to 0:', userError);
      setUnreadCount(0);
      return;
    }
    
    console.log('🔄 Using fresh user from supabase:', currentUser.id);
    
    try {
      console.log('🔄 Refreshing unread count for user:', currentUser.id);
      
      // Try the count query first
      const { count, error } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .eq('recipient_id', currentUser.id)
        .eq('is_read', false);
      
      console.log('🔄 Count query result:', { count, error });
      
      if (error) {
        console.error('🔄 Count query failed, trying alternative:', error);
        
        // Alternative: fetch and count manually
        const { data: unreadMessages, error: altError } = await supabase
          .from('messages')
          .select('id')
          .eq('recipient_id', currentUser.id)
          .eq('is_read', false);
        
        console.log('🔄 Alternative query result:', { unreadMessages, altError });
        
        if (!altError) {
          const manualCount = unreadMessages?.length || 0;
          console.log('🔄 Using manual count:', manualCount);
          setUnreadCount(manualCount);
        } else {
          console.error('🔄 Alternative query also failed:', altError);
          setUnreadCount(0);
        }
      } else {
        console.log('🔄 Using count query result:', count || 0);
        setUnreadCount(count || 0);
      }
    } catch (err) {
      console.error('🔄 Error refreshing unread count:', err);
      setUnreadCount(0);
    }
  };
  
  // Expose refresh function globally
  useEffect(() => {
    window.refreshUnreadCount = refreshUnreadCount;
    console.log('🔄 refreshUnreadCount function exposed globally');
  }, [user]);

  // Fetch unread message count and subscribe for real-time updates
  useEffect(() => {
    // Expose supabase globally for debugging
    window.supabase = supabase;
    
    let subscription;
    async function fetchUnread() {
      // Get fresh user from supabase
      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !currentUser) {
        console.log('No user found, setting unread count to 0:', userError);
        return setUnreadCount(0);
      }
      
      console.log('Fetching unread count for user:', currentUser.id);
      
      try {
        console.log('🔍 Starting unread count fetch for user:', currentUser.id);
        
        // First, let's check if we can access the messages table at all
        const { data: testData, error: testError } = await supabase
          .from('messages')
          .select('id')
          .limit(1);
        
        console.log('📊 Messages table access test:', { testData, testError });
        
        if (testError) {
          console.error('❌ Cannot access messages table:', testError);
          setUnreadCount(0);
          return;
        }
        
        // Check all messages for this user (both sent and received)
        const { data: allMessages, error: allError } = await supabase
          .from('messages')
          .select('id, sender_id, recipient_id, is_read, created_at')
          .or(`sender_id.eq.${currentUser.id},recipient_id.eq.${currentUser.id}`)
          .order('created_at', { ascending: false })
          .limit(10);
        
        console.log('📨 All messages for user:', { allMessages, allError });
        
        // Count unread messages using count query
        const { count, error } = await supabase
          .from('messages')
          .select('*', { count: 'exact', head: true })
          .eq('recipient_id', currentUser.id)
          .eq('is_read', false);
        
        console.log('🔢 Unread count query result:', { count, error });
        
        // Alternative approach - fetch and count manually
        const { data: unreadMessages, error: unreadError } = await supabase
          .from('messages')
          .select('id, sender_id, recipient_id, is_read')
          .eq('recipient_id', currentUser.id)
          .eq('is_read', false);
        
        console.log('📬 Unread messages (manual count):', { 
          unreadMessages, 
          unreadError, 
          manualCount: unreadMessages?.length || 0 
        });
        
        // Try different is_read values (in case it's stored as string)
        const { data: unreadMessagesAlt, error: unreadErrorAlt } = await supabase
          .from('messages')
          .select('id, sender_id, recipient_id, is_read')
          .eq('recipient_id', currentUser.id)
          .or('is_read.is.null,is_read.eq.false,is_read.eq."false",is_read.eq.0,is_read.eq."0"');
        
        console.log('📬 Unread messages (alternative):', { 
          unreadMessagesAlt, 
          unreadErrorAlt, 
          manualCountAlt: unreadMessagesAlt?.length || 0 
        });
        
        // Try a different approach - check if is_read column exists
        const { data: columnTest, error: columnError } = await supabase
          .from('messages')
          .select('is_read')
          .eq('recipient_id', currentUser.id)
          .limit(1);
        
        console.log('🔧 Column test (is_read):', { columnTest, columnError });
        
        // Use the alternative count if it found more messages
        const finalCount = unreadMessagesAlt?.length || unreadMessages?.length || count || 0;
        console.log('✅ Final unread count:', finalCount);
        console.log('🎯 Setting unread count state to:', finalCount);
        setUnreadCount(finalCount);
        
      } catch (err) {
        console.error('💥 Unexpected error in fetchUnread:', err);
        setUnreadCount(0);
      }
    }
    
    fetchUnread();
    
    // Set up subscription and auto-refresh after getting the user
    const setupSubscriptionAndAutoRefresh = async () => {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (currentUser) {
        console.log('📡 Setting up real-time subscription for user:', currentUser.id);
        
        // Real-time subscription for immediate updates
        subscription = supabase
          .channel(`unread-messages:${currentUser.id}`)
          .on('postgres_changes', {
            event: '*',
            schema: 'public',
            table: 'messages',
            filter: `recipient_id=eq.${currentUser.id}`
          }, (payload) => {
            console.log('📨 Message change detected:', payload);
            console.log('📨 Event type:', payload.eventType);
            console.log('📨 New record:', payload.new);
            console.log('📨 Old record:', payload.old);
            
            // Refresh unread count when messages change
            setTimeout(() => {
              console.log('🔄 Refreshing unread count after message change...');
              refreshUnreadCount();
            }, 500);
          })
          .subscribe((status) => {
            console.log('📡 Subscription status:', status);
            if (status === 'SUBSCRIBED') {
              console.log('✅ Successfully subscribed to message changes');
            } else if (status === 'CHANNEL_ERROR') {
              console.error('❌ Subscription channel error');
              // Retry subscription after error
              setTimeout(() => setupSubscriptionAndAutoRefresh(), 5000);
            } else if (status === 'TIMED_OUT') {
              console.error('❌ Subscription timed out');
              // Retry subscription after timeout
              setTimeout(() => setupSubscriptionAndAutoRefresh(), 5000);
            }
          });
        
        // Auto-refresh every 30 seconds as backup (in case real-time fails)
        const autoRefreshInterval = setInterval(() => {
          console.log('🔄 Auto-refreshing unread count (30s interval)...');
          refreshUnreadCount();
        }, 30000);
        
        // Store interval for cleanup
        window.autoRefreshInterval = autoRefreshInterval;
        
        // Refresh when user comes back to tab (focus/visibility API)
        const handleVisibilityChange = () => {
          if (!document.hidden) {
            console.log('🔄 Tab became visible, refreshing unread count...');
            setTimeout(() => refreshUnreadCount(), 1000);
          }
        };
        
        const handleFocus = () => {
          console.log('🔄 Window focused, refreshing unread count...');
          setTimeout(() => refreshUnreadCount(), 1000);
        };
        
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);
        
        // Store event listeners for cleanup
        window.unreadCountEventListeners = {
          visibilitychange: handleVisibilityChange,
          focus: handleFocus
        };
      }
    };
    
    setupSubscriptionAndAutoRefresh();
    
    return () => {
      if (subscription) {
        console.log('Unsubscribing from messages channel');
        subscription.unsubscribe();
      }
      if (window.autoRefreshInterval) {
        console.log('Clearing auto-refresh interval');
        clearInterval(window.autoRefreshInterval);
        window.autoRefreshInterval = null;
      }
      if (window.unreadCountEventListeners) {
        console.log('Removing event listeners');
        document.removeEventListener('visibilitychange', window.unreadCountEventListeners.visibilitychange);
        window.removeEventListener('focus', window.unreadCountEventListeners.focus);
        window.unreadCountEventListeners = null;
      }
    };
  }, [user]);
  
  return (
    <LanguageProvider>
      <AuthProvider>
        <div className="app-shell">
          {!isLogin && !isCallback && !isChat && <Header searchQuery={searchQuery} setSearchQuery={setSearchQuery} unreadCount={unreadCount} />}
          <main className="main-content container-center">
            <Routes>
            <Route path="/" element={<Login />} />
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/browse" element={
              <ProtectedRoute>
                <Browse searchQuery={searchQuery} />
              </ProtectedRoute>
            } />
            <Route path="/my-listings" element={
              <ProtectedRoute>
                <MyListings />
              </ProtectedRoute>
            } />
            <Route path="/favorites" element={
              <ProtectedRoute>
                <Favorites />
              </ProtectedRoute>
            } />
            <Route path="/messages" element={
              <ProtectedRoute>
                <Conversations />
              </ProtectedRoute>
            } />
            <Route path="/messages/:id" element={
              <ProtectedRoute>
                <Chat />
              </ProtectedRoute>
            } />
            <Route path="/listing/:id" element={
              <ProtectedRoute>
                <ListingDetail />
              </ProtectedRoute>
            } />
            <Route path="/create" element={
              <ProtectedRoute>
                <CreateListing />
              </ProtectedRoute>
            } />
            <Route path="/edit/:id" element={
              <ProtectedRoute>
                <EditListing />
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } />
            <Route path="/purchase-history" element={
              <ProtectedRoute>
                <PurchaseHistory />
              </ProtectedRoute>
            } />
            <Route path="/sales-history" element={
              <ProtectedRoute>
                <SalesHistory />
              </ProtectedRoute>
            } />
          </Routes>
        </main>
        {!isLogin && !isCallback && !isChat && <BottomNav unreadCount={unreadCount} />}
        
        {/* Floating AI Assistant Button */}
        {!isLogin && !isCallback && !isChat && <FloatingAIButton />}
        </div>
      </AuthProvider>
    </LanguageProvider>
  );
}